import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { DEMO_VIDEO_URL } from "@/data/constants";
import SEO from "@/components/seo/SEO";
import { Play, Pause, Volume2, VolumeX, Maximize, ArrowLeft, Settings } from "lucide-react";

export default function Watch() {
  const { slug } = useParams();
  const nav = useNavigate();
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [title, setTitle] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const hideTimer = useRef(null);

  useEffect(() => { api.get(`/titles/${slug}`).then(({ data }) => setTitle(data)).catch(() => nav("/")); }, [slug, nav]);

  const togglePlay = () => {
    const v = videoRef.current; if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  const toggleMute = () => { const v = videoRef.current; if (!v) return; v.muted = !v.muted; setMuted(v.muted); };
  const onTime = () => { const v = videoRef.current; if (!v) return; setProgress(v.currentTime); setDuration(v.duration || 0); };
  const seek = (e) => {
    const v = videoRef.current; if (!v || !v.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    v.currentTime = pct * v.duration;
  };
  const goFull = () => {
    const el = containerRef.current;
    if (!document.fullscreenElement) el?.requestFullscreen?.(); else document.exitFullscreen?.();
  };
  const showThenHide = () => {
    setShowControls(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setShowControls(false), 3200);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space") { e.preventDefault(); togglePlay(); }
      if (e.code === "KeyM") toggleMute();
      if (e.code === "KeyF") goFull();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const fmt = (s) => {
    if (!isFinite(s)) return "0:00";
    const m = Math.floor(s / 60); const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <div ref={containerRef} className="relative bg-black min-h-screen flex flex-col" data-testid="watch-page" onMouseMove={showThenHide}>
      <SEO
        title={title ? `Watch ${title.title}` : "Watch"}
        description={title ? `Now streaming ${title.title} on Your Channel Network.` : "Watch on Your Channel Network."}
        path={`/watch/${slug}`}
        noindex
      />
      <video
        ref={videoRef}
        src={DEMO_VIDEO_URL}
        className="w-full h-screen object-contain bg-black"
        onClick={togglePlay}
        onTimeUpdate={onTime}
        onLoadedMetadata={onTime}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        playsInline
        autoPlay
        data-testid="watch-video"
      />

      {/* Top overlay */}
      <div className={`absolute top-0 left-0 right-0 p-6 flex items-center justify-between bg-gradient-to-b from-black/80 to-transparent transition-opacity ${showControls ? "opacity-100" : "opacity-0"}`}>
        <div className="flex items-center gap-3">
          <button onClick={() => nav(-1)} data-testid="watch-back-btn" className="h-10 w-10 rounded-full ycn-glass hover:bg-white/20 flex items-center justify-center">
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="text-xs uppercase tracking-widest text-white/60">Now Watching · <span className="text-orange-300 font-semibold">Demo Video</span></div>
            <div className="font-display text-lg md:text-2xl font-bold">{title?.title || slug}</div>
          </div>
        </div>
        <Link to={`/title/${slug}`} className="text-sm text-white/70 hover:text-white">Details →</Link>
      </div>

      {/* Bottom controls */}
      <div className={`absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 via-black/40 to-transparent transition-opacity ${showControls ? "opacity-100" : "opacity-0"}`}>
        <div onClick={seek} className="h-1.5 w-full bg-white/15 rounded-full cursor-pointer group" data-testid="watch-progress">
          <div className="h-full rounded-full bg-gradient-to-r from-violet-500 via-blue-500 to-orange-500 group-hover:h-2 transition-all" style={{ width: duration ? `${(progress / duration) * 100}%` : "0%" }} />
        </div>
        <div className="mt-4 flex items-center gap-4">
          <button onClick={togglePlay} data-testid="watch-play-btn" className="h-12 w-12 rounded-full bg-white text-black flex items-center justify-center hover:scale-105 transition">
            {playing ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" />}
          </button>
          <button onClick={toggleMute} data-testid="watch-mute-btn" className="h-10 w-10 rounded-full ycn-glass hover:bg-white/20 flex items-center justify-center">
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <span className="text-sm text-white/70 tabular-nums font-mono">{fmt(progress)} / {fmt(duration)}</span>
          <div className="ml-auto flex items-center gap-3">
            <button className="h-10 w-10 rounded-full ycn-glass hover:bg-white/20 flex items-center justify-center opacity-70" aria-label="Settings">
              <Settings size={16} />
            </button>
            <button onClick={goFull} data-testid="watch-fullscreen-btn" className="h-10 w-10 rounded-full ycn-glass hover:bg-white/20 flex items-center justify-center">
              <Maximize size={16} />
            </button>
          </div>
        </div>
        <div className="mt-3 text-[11px] uppercase tracking-widest text-white/40">
          Demo playback — replace with hosted YCN content in production
        </div>
      </div>
    </div>
  );
}
